import { makeObservable, observable } from 'mobx';
import { z } from 'zod';
import type { RequireAllProps } from '../../../../store/utils/Dto';
import type { EventOrValue } from '../../../../store/utils/extractValue';
import type { AccountSaveMethod } from '../types';
import Service, { type ServiceDto } from './Service';
import {
  CDN_PROVIDERS,
  type CdnProvider,
  STORAGE_SERVICES,
  type StorageProvider,
} from './types';

export default class Cdn extends Service<CdnDto> {
  static Schema = z
    .object({
      bucket: z.string(),
      cname: z.string(),
      enabled: z.boolean().default(false),
      passphrase: z.string(),
      cdnProvider: z.enum(CDN_PROVIDERS).optional(),
      s3Provider: z.enum(STORAGE_SERVICES).optional(),
    })
    // A disabled CDN keeps its blank fields, otherwise it can't be turned off
    .superRefine((data, ctx) => {
      if (!data.enabled) {
        return;
      }

      for (const field of ['bucket', 'cname', 'passphrase'] as const) {
        if (!data[field]) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: `${field} is required`,
          });
        }
      }
    });

  bucket = '';
  cname = '';
  enabled = false;
  passphrase = '';

  cdnProvider: CdnProvider = 'bunnycdn';
  s3Provider: StorageProvider = 'amazonaws.com';

  constructor(dto: CdnDto = {} as any, onSave?: AccountSaveMethod) {
    super(dto, onSave);

    makeObservable(this, {
      bucket: observable,
      cdnProvider: observable,
      cname: observable,
      enabled: observable,
      passphrase: observable,
      s3Provider: observable,
    });

    Object.assign(this, dto);
  }

  /** The CDN form has its own Save button, so it doesn't save on change */
  handleChange(e: EventOrValue) {
    this.setValue(e);
  }

  get isEmpty(): boolean {
    if (!this.cname || !this.passphrase) {
      return true;
    }

    // noinspection RedundantIfStatementJS
    if (this.cdnProvider === 'bunnycdn' && this.cname.includes('b-cdn.net')) {
      return false;
    }

    return true;
  }

  toJSON(): RequireAllProps<CdnDto> {
    return {
      ...super.toJSON(),
      provider: 'cdn',
      bucket: this.bucket,
      cname: this.cname,
      enabled: this.enabled,
      passphrase: this.passphrase === '*****' ? undefined : this.passphrase,
      cdnProvider: this.cdnProvider,
      s3Provider: this.s3Provider,
    };
  }
}

export interface CdnDto extends ServiceDto {
  bucket?: string;
  cdnProvider?: CdnProvider;
  cname?: string;
  enabled?: boolean;
  passphrase?: string;
  s3Provider?: StorageProvider;
}
